import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import '../styles/Dashboard.css';

export default function Dashboard() {
  const [experiments, setExperiments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [archivingId, setArchivingId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    async function getExperiments() {
      setLoading(true);
      // First get experiments, then we'll fetch user profiles separately
      let query = supabase
        .from('experiments')
        .select('*');

      if (showArchived) {
        query = query.eq('is_archived', true);
      } else {
        query = query.eq('is_archived', false);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching experiments:', error);
        setLoading(false);
      } else {
        // Fetch user profiles for all unique user_ids
        const userIds = [...new Set(data.map(exp => exp.user_id).filter(id => id))];
        
        if (userIds.length > 0) {
          const { data: profiles, error: profileError } = await supabase
            .from('user_profiles')
            .select('id, full_name, email')
            .in('id', userIds);
          
          if (!profileError && profiles) {
            // Map profiles to experiments
            const profileMap = profiles.reduce((acc, profile) => {
              acc[profile.id] = profile;
              return acc;
            }, {});
            
            // Add user_profiles to each experiment
            const experimentsWithProfiles = data.map(exp => ({
              ...exp,
              user_profiles: profileMap[exp.user_id] || null
            }));
            
            setExperiments(experimentsWithProfiles);
          } else {
            setExperiments(data);
          }
        } else {
          setExperiments(data);
        }
        setLoading(false);
      }
    }

    getExperiments();
  }, [showArchived]);

  const handleArchiveToggle = async (e, id, currentStatus) => {
    e.stopPropagation(); // Prevent row click
    setArchivingId(id);
    const { error } = await supabase.from('experiments').update({ is_archived: !currentStatus }).eq('id', id);
    if (error) {
      console.error('Error updating archive status:', error);
      alert('Error updating archive status: ' + error.message);
    } else {
      // Remove from current list instead of updating
      setExperiments(prevExperiments =>
        prevExperiments.filter(exp => exp.id !== id)
      );
    }
    setArchivingId(null);
  };

  const handleRowClick = (id) => {
    navigate(`/experiment/${id}`);
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1 className="dashboard-title">Experiments Dashboard</h1>
        <div className="dashboard-actions">
          <div className="tab-buttons">
            <button
              className={`tab-button ${!showArchived ? 'active' : ''}`}
              onClick={() => setShowArchived(false)}
            >
              Active
            </button>
            <button
              className={`tab-button ${showArchived ? 'active' : ''}`}
              onClick={() => setShowArchived(true)}
            >
              Archived
            </button>
          </div>
          <Link to="/create" className="btn btn-primary">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            New Experiment
          </Link>
        </div>
      </div>
      
      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
        </div>
      ) : experiments.length === 0 ? (
        <div className="empty-state">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 2v6"></path>
            <path d="M15 2v6"></path>
            <path d="M12 2v6"></path>
            <rect x="8" y="8" width="8" height="10" rx="1"></rect>
            <path d="M8 8V7a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v1"></path>
            <line x1="10" y1="11" x2="10" y2="15"></line>
            <line x1="14" y1="11" x2="14" y2="15"></line>
          </svg>
          <p className="empty-state-text">No {showArchived ? 'archived' : 'active'} experiments found</p>
          {!showArchived && (
            <Link to="/create" className="btn btn-primary mt-3">Create Your First Experiment</Link>
          )}
        </div>
      ) : (
        <div className="table-container">
          <table className="experiments-table">
            <thead>
              <tr>
                <th>Sample ID</th>
                <th>Supplier Name</th>
                <th>Supplier Location</th>
                <th>Bags</th>
                <th>Quality</th>
                <th>Supplied Date</th>
                <th>Experiment Date</th>
                <th>Operator</th>
                <th>Created By</th>
                <th>Moisture %</th>
                <th>FFA %</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {experiments.map((exp) => (
                <tr key={exp.id} onClick={() => handleRowClick(exp.id)} className="table-row">
                  <td className="sample-id">{exp.sample_id}</td>
                  <td>{exp.supplier_name}</td>
                  <td>{exp.supplier_location}</td>
                  <td className="text-center">{exp.number_of_bags}</td>
                  <td>{exp.inspected_quality}</td>
                  <td className="date">{new Date(exp.supplied_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}</td>
                  <td className="date">{exp.experiment_date ? 
                    new Date(exp.experiment_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 
                    new Date(exp.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}</td>
                  <td className="text-center">{exp.operator_initials}</td>
                  <td className="text-center">{exp.user_profiles?.full_name || exp.user_profiles?.email?.split('@')[0] || '-'}</td>
                  <td className="text-center">{exp.moisture_percentage || '-'}</td>
                  <td className="ffa-value">{parseFloat(exp.calculated_ffa).toFixed(2)}</td>
                  <td>
                    <button 
                      className="action-button"
                      onClick={(e) => handleArchiveToggle(e, exp.id, exp.is_archived)}
                      disabled={archivingId === exp.id}
                    >
                      {archivingId === exp.id ? (
                        <span className="small-loader"></span>
                      ) : (
                        <>
                          {exp.is_archived ? (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                              <polyline points="10 11 12 13 14 11"></polyline>
                            </svg>
                          ) : (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="21 8 21 21 3 21 3 8"></polyline>
                              <rect x="1" y="3" width="22" height="5"></rect>
                              <line x1="10" y1="12" x2="14" y2="12"></line>
                            </svg>
                          )}
                          <span>{exp.is_archived ? 'Unarchive' : 'Archive'}</span>
                        </>
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}